import { useEffect, useState } from 'react';

import { useTypedTranslation } from '~/shared/hooks';
import type { Locale } from '~/universal/constants';

import type { PopularCourseMenuItem, PopularCoursesPair } from './helpers/popular-courses-cache';
import {
  getCachedPopularCoursesPair,
  LIMIT_OF_COURSES,
  popularCoursesPairCache,
} from './helpers/popular-courses-cache';

type UsePopularCoursesProps = Readonly<{
  lng: Locale;
}>;

type PopularCourseListItem =
  | PopularCourseMenuItem
  | Readonly<{
      _id: string;
      item: null;
    }>;

type PopularCoursesState = Readonly<{
  freeItems: PopularCourseListItem[];
  paidItems: PopularCourseListItem[];
  freeLoading: boolean;
  paidLoading: boolean;
  freeError: boolean;
  paidError: boolean;
}>;

const createPopularCourseSkeletons = (): PopularCourseListItem[] => {
  return Array.from({ length: LIMIT_OF_COURSES }, () => ({
    _id: crypto.randomUUID(),
    item: null,
  }));
};

const createInitialState = (lng: Locale): PopularCoursesState => {
  const cachedPair = getCachedPopularCoursesPair(lng);

  if (cachedPair) {
    return {
      freeItems: cachedPair.freeCourses,
      paidItems: cachedPair.paidCourses,
      freeLoading: false,
      paidLoading: false,
      freeError: false,
      paidError: false,
    };
  }

  return {
    freeItems: createPopularCourseSkeletons(),
    paidItems: createPopularCourseSkeletons(),
    freeLoading: true,
    paidLoading: true,
    freeError: false,
    paidError: false,
  };
};

const usePopularCourses = ({ lng }: UsePopularCoursesProps) => {
  const t = useTypedTranslation(['text', 'link']);
  const [state, setState] = useState<PopularCoursesState>(() => createInitialState(lng));

  useEffect(() => {
    const cachedPair = getCachedPopularCoursesPair(lng);

    if (cachedPair) {
      setState({
        freeItems: cachedPair.freeCourses,
        paidItems: cachedPair.paidCourses,
        freeLoading: false,
        paidLoading: false,
        freeError: false,
        paidError: false,
      });

      return;
    }

    let cancelled = false;

    const syncCourses = async () => {
      try {
        const data: PopularCoursesPair = await popularCoursesPairCache(lng);

        if (cancelled) {
          return;
        }

        setState({
          freeItems: data.freeCourses,
          paidItems: data.paidCourses,
          freeLoading: false,
          paidLoading: false,
          freeError: false,
          paidError: false,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error('Failed to fetch popular courses:', error);

        setState({
          freeItems: [],
          paidItems: [],
          freeLoading: false,
          paidLoading: false,
          freeError: true,
          paidError: true,
        });
      }
    };

    void syncCourses();

    return () => {
      cancelled = true;
    };
  }, [lng]);

  return {
    t,
    freeItems: state.freeItems,
    paidItems: state.paidItems,
    freeLoading: state.freeLoading,
    paidLoading: state.paidLoading,
    freeError: state.freeError,
    paidError: state.paidError,
  };
};

export default usePopularCourses;
